export enum WorkflowPostActionProcessorActionType {
  RECORD_ACTION = 'record_action',
}

export enum WorkflowDataSourceProvider {
  INTEGRATION_APP = 'integration_app',
}

export enum WorkflowDataSourceEventType {
  RECORD_CREATED = 'event_record_created',
}

export enum WorkflowActionProvider {
  INTEGRATION_APP = 'integration_app',
  FLUENTS = 'fluents',
}

export enum FluentsActionKey {
  CREATE_CALL = 'create_call',
}

export enum WorkflowStepType {
  ACTION = 'action',
  PAUSE = 'pause',
}

export enum GroupOperators {
  AND = 'AND',
  OR = 'OR',
}

export enum FieldOperators {
  EQ = 'EQ',
  NEQ = 'NEQ',
  GT = 'GT',
  GTE = 'GTE',
  LT = 'LT',
  LTE = 'LTE',
  CONTAINS = 'CONTAINS',
  NOT_CONTAINS = 'NOT_CONTAINS',
}

export type WorkflowPostActionProcessorConfigDto = {
  action_type: WorkflowPostActionProcessorActionType;
};

export type FieldFilterDto = {
  field: string;
  operator: FieldOperators;
  value: string | number | boolean | Date;
};

export type GroupFilterDto = {
  groupOperator: GroupOperators;
  children: Array<GroupFilterDto | FieldFilterDto>;
};

export type WorkflowDataSourceConfigDto = {
  source_provider: WorkflowDataSourceProvider;
  source_id: string;
  event_types: WorkflowDataSourceEventType[];
  event_filter?: GroupFilterDto;
  field_mapping: Record<string, unknown>;
  post_action_processors: WorkflowPostActionProcessorConfigDto[];
};

export type IntegrationAppActionConfigDto = {
  integration_key: string;
  action_key: string;
};

export type FluentsActionConfigDto = {
  action_key: FluentsActionKey;
};

export type WorkflowActionProviderConfigDto =
  | IntegrationAppActionConfigDto
  | FluentsActionConfigDto;

export type WorkflowStepActionConfigDto = {
  action_provider: WorkflowActionProvider;
  action_config: WorkflowActionProviderConfigDto;
};

export type WorkflowStepPauseConfigDto = Record<string, never>;

export type WorkflowStepDto = {
  reference_id: string;

  label: string;

  type: WorkflowStepType;

  config: WorkflowStepActionConfigDto | WorkflowStepPauseConfigDto;

  params: Record<string, unknown>;
};

export enum DayOfWeek {
  monday = 'monday',
  tuesday = 'tuesday',
  wednesday = 'wednesday',
  thursday = 'thursday',
  friday = 'friday',
  saturday = 'saturday',
  sunday = 'sunday',
}

export type AllowedTimesEntryDto = {
  day: DayOfWeek;
  startTime: string;
  endTime: string;
};

export type WorkflowScheduleDto = {
  timeZone: string;
  startDate: string;
  allowedTimes: AllowedTimesEntryDto[];
};

export type WorkflowConfigDto = {
  params_schema: Record<string, unknown>;

  context?: Record<string, unknown>;

  data_sources: WorkflowDataSourceConfigDto[];

  steps: WorkflowStepDto[];

  schedule: WorkflowScheduleDto;
};

export type CreateWorkflowDto = {
  referenceId: string;

  label: string;

  description: string;

  config: WorkflowConfigDto;
  contactWindowId?: string;
};

export type Workflow = CreateWorkflowDto & {
  id: string;
  version: number;
  active: boolean;
  environmentId: string;
  createdAt: Date;
  updatedAt: Date;
};

export enum WorkflowStatus {
  WORKFLOW_ACTIVE = 'workflow_active',
  WORKFLOW_PAUSED = 'workflow_paused',
  WORKFLOW_DELETED = 'workflow_deleted',
}

export type WorkflowWithStatus = Workflow & {
  status: WorkflowStatus;
};

export enum WorkflowExecutionStatus {
  EXECUTION_STARTED = 'workflow_execution_started',
  EXECUTION_CANCELED = 'workflow_execution_canceled',
}

export enum WorkflowExecutionCancelationReason {
  REASON_USER_REQUESTED = 'cancelation_reason_user_requested',
  REASON_WORKFLOW_DELETED = 'cancelation_reason_workflow_deleted',
  REASON_CALL_ANSWERED = 'cancelation_reason_call_answered',
  REASON_INCOMING_CALL = 'cancelation_reason_incoming_call',
  REASON_ACTION_TERMINATED = 'cancelation_reason_action_terminated',
  REASON_ERROR = 'cancelation_reason_error',
}

export type WorkflowExecutionStatusDetails = {
  updated_at: string; // ISO timestamp of the status change
  cancelation_reason?: WorkflowExecutionCancelationReason;
  error?: string;
};

export type WorkflowExecutionMetadata = {
  execution_params: Record<string, unknown>;
};

export type WorkflowExecutionThreadActionHistoryRecord = {
  action_provider: WorkflowActionProvider;
  action_config: WorkflowActionProviderConfigDto;
  completed_at: string;
  result?: Record<string, unknown>;
  error?: string;
};

export type WorkflowExecutionThreadPendingAction = {
  action_provider: WorkflowActionProvider;
  action_config: WorkflowActionProviderConfigDto;
  execution_id: string;
  completion_timeout_seconds: number;
  result?: Record<string, unknown>;
  error?: string;
};

export type WorkflowExecutionThreadWithComputedStatus = {
  id: string;
  created_at: Date;
  updated_at: Date;
  status: WorkflowExecutionThreadStatus;
  resume_at: Date | null;
  action_history: WorkflowExecutionThreadActionHistoryRecord[];
  pending_action: WorkflowExecutionThreadPendingAction | null;

  execution_id: string;
  execution_status: WorkflowExecutionStatus;
  execution_cancelation_reason?: WorkflowExecutionCancelationReason;
  execution_params: Record<string, unknown>;

  workflow_id: string;
  workflow_label: string;
  workflow_reference_id: string;
};

export type PaginatedResponse<T> = {
  items: T[];
  page: number;
  size: number;
  has_more: boolean;
  total: number;
  total_is_estimated: boolean;
};

export type WorkflowDataSourceRecordDto = {
  source_provider: WorkflowDataSourceProvider;
  source_id: string;
  id: string;
  data_source_key: string;
};

export type CreateWorkflowExecutionDto = {
  workflowId?: string;
  workflowReferenceId?: string;
  params: Record<string, unknown>;
  sourceRecord?: WorkflowDataSourceRecordDto;
};

export enum WorkflowExecutionThreadStatus {
  RUNNING = 'thread_running',
  RATE_LIMITED = 'thread_rate_limited',
  PAUSED = 'thread_paused', // TODO: remove this once we have confirmed that no threads are in this status
  CANCELED = 'thread_canceled',
  ERRORED = 'thread_errored',
  COMPLETED = 'thread_completed',
}

export enum ExecutionDeduplicationAction {
  UPDATE = 'update',
  SKIP = 'skip',
}

export type ExecutionDeduplicationConfigDto = {
  uniqueParamKeys: string[];
  onDuplicate: ExecutionDeduplicationAction;
};

export type BatchExecutionRequestDto = {
  params: Record<string, unknown>;
  sourceRecord?: WorkflowDataSourceRecordDto;
};

export type BatchCreateWorkflowExecutionDto = {
  workflowReferenceId: string;
  executions: BatchExecutionRequestDto[];
  deduplicationConfig?: ExecutionDeduplicationConfigDto;
};

export type BatchExecutionResponseDto = {
  id?: string;
  error?: string;
  duplicateResolutionAction?: ExecutionDeduplicationAction;
};

export type BatchCreateWorkflowExecutionResponseDto = {
  executions: BatchExecutionResponseDto[];
};

export type WorkflowExecutionThreadFindAllFilters = {
  statuses?: Array<WorkflowExecutionThreadStatus>;
  workflowReferenceId?: string;
  executionParams?: Record<string, string>;
};

export enum WorkflowTimezone {
  CONTACT_LOCAL = 'contact-local',
  AMERICA_LOS_ANGELES = 'America/Los_Angeles',
  AMERICA_DENVER = 'America/Denver',
  AMERICA_CHICAGO = 'America/Chicago',
  AMERICA_NEW_YORK = 'America/New_York',
}

export type WorkflowContactWindow = {
  id: string;
  label: string;
  description?: string;
  days?: number[];
  startTime: string;
  endTime: string;
  primaryTz: WorkflowTimezone;
  fallbackTz?: WorkflowTimezone;
  usHolidays?: boolean;
  blackoutDates?: string[];
};
